import { useEffect, useRef } from 'react';
import { supabase } from '../../supabase';
import { useAuth } from '../../AuthContext';
import useAssuranceSettled from '../../hooks/auth/useAssuranceSettled';
import { withTimeout } from '../../utils/async/withTimeout';
import { setThemePreference, getThemeChangeCount, getThemePreference } from '../../ui/theme/themeController';
import { subscribeAppResume } from '../../utils/ui/appResumeGate';
import { validPref, readDevicePrefOwner } from '../../ui/theme/themeMode';

/** Max wait for the one preference read. */
const READ_TIMEOUT_MS = 8000;
/** Min gap between foreground re-reads (the gate already needs 60s away; this is a second belt). */
const RESUME_MIN_GAP_MS = 60 * 1000;

/**
 * Loads the signed-in user's saved theme preference (KAN-304 step 4). Renders nothing.
 *
 * ONE read per user + account kind (budget: 1 call per sign-in / cold start; no polling, no realtime): the own
 * `profiles` row for tenant users, the own `platform_users` row for platform users. It waits for the
 * session's final 2FA level AND for the auth context to finish resolving the role (platform or tenant), so a
 * platform user is never read from `profiles`. A saved value is applied and copied to the device with the
 * user's id. A NULL value means "system" for this user: if the device copy belongs to someone else (a session
 * that ended without our sign-out) it is reset, so the next person never inherits it. An error or timeout
 * keeps the copy. If the user already picked a theme while the read was out, the read is dropped.
 * LIVE SYNC: also one re-read per return to the foreground via `subscribeAppResume` (never its own listener),
 * throttled to 60s; same rules, except a NULL result never resets anything on a resume. Call budget: <=1 read
 * per resume, so <=60/hour worst case per active user (at 500 companies the gate's 60s-away rule makes it far lower).
 * Mounted by `SessionScopedShell`.
 */
export default function ThemePreferenceSync({ userId, isPlatformUser = false }) {
  const { settled } = useAssuranceSettled(userId);
  const { loading, tenantLoading } = useAuth();
  const rolesResolved = loading === false && tenantLoading === false;
  const readFor = useRef(null);

  useEffect(() => {
    if (!userId || !settled || !rolesResolved) return undefined;
    const key = `${userId}:${isPlatformUser}`;
    let cancelled = false;
    let lastReadAt = 0;
    const read = async (force) => {
      const now = Date.now();
      if (!force && now - lastReadAt < RESUME_MIN_GAP_MS) return;
      lastReadAt = now;
      const startedAt = getThemeChangeCount();
      try {
        const query = isPlatformUser
          ? supabase.from('platform_users').select('preferred_theme').eq('user_id', userId).maybeSingle()
          : supabase.from('profiles').select('preferred_theme').eq('id', userId).maybeSingle();
        const { data, error } = await withTimeout(query, READ_TIMEOUT_MS);
        if (cancelled || error || getThemeChangeCount() !== startedAt) return;
        const value = validPref(data?.preferred_theme);
        if (value) {
          // The first read of a session always re-stamps the copy with THIS user's id, even when the value matches.
          if (force || value !== getThemePreference()) setThemePreference(value, userId);
        } else if (force && readDevicePrefOwner() !== userId) setThemePreference('system', userId);
      } catch { /* keep the device copy */ }
    };
    if (readFor.current !== key) {
      readFor.current = key;
      read(true);
    }
    // Another device may have changed the theme: re-read on return to the foreground (shared resume gate).
    const unsubscribe = subscribeAppResume(() => { read(false); });
    return () => { cancelled = true; unsubscribe(); };
  }, [userId, settled, rolesResolved, isPlatformUser]);

  return null;
}
